import {
  type CreateTransferRequest,
  type TransferApprovalRequest,
  type TransferDetail,
  TransferDetailSchema,
  type TransferFacets,
  TransferFacetsSchema,
  type TransferListQuery,
  type TransferListResponse,
  TransferListResponseSchema,
  type TransferPhoto,
  type UpdateTransferRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

export const listTransfers = async (query: TransferListQuery): Promise<TransferListResponse> => {
  const { data } = await apiClient.get<unknown>('/transfers', { params: query });
  return TransferListResponseSchema.parse(data);
};

export const getTransfer = async (id: string): Promise<TransferDetail> => {
  const { data } = await apiClient.get<unknown>(`/transfers/${id}`);
  return TransferDetailSchema.parse(data);
};

export const createTransfer = async (payload: CreateTransferRequest): Promise<TransferDetail> => {
  const { data } = await apiClient.post<unknown>('/transfers', payload);
  return TransferDetailSchema.parse(data);
};

export const updateTransfer = async (
  id: string,
  payload: UpdateTransferRequest,
): Promise<TransferDetail> => {
  const { data } = await apiClient.patch<unknown>(`/transfers/${id}`, payload);
  return TransferDetailSchema.parse(data);
};

export const approveTransfer = async (
  id: string,
  payload: TransferApprovalRequest,
): Promise<TransferDetail> => {
  const { data } = await apiClient.post<unknown>(`/transfers/${id}/approval`, payload);
  return TransferDetailSchema.parse(data);
};

export const deleteTransfer = async (id: string): Promise<void> => {
  await apiClient.delete(`/transfers/${id}`);
};

export const duplicateTransfer = async (id: string): Promise<TransferDetail> => {
  const { data } = await apiClient.post<unknown>(`/transfers/${id}/duplicate`);
  return TransferDetailSchema.parse(data);
};

export const bulkDeleteTransfers = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/transfers/bulk-delete', { ids });
  return data;
};

/**
 * Facets for the Country / City list filters - only the countries and cities
 * actually used by transfers (not the whole geography master). Grows as
 * transfers for new places are added.
 */
export const getTransferFacets = async (): Promise<TransferFacets> => {
  const { data } = await apiClient.get<unknown>('/transfers/facets');
  return TransferFacetsSchema.parse(data);
};

/**
 * Apply the same cover + gallery images to many transfers at once. There is no
 * server-side bulk-media endpoint, so this fans out a partial PATCH per id
 * (`photos` + `galleryImageMediaIds` only, everything else left unchanged) and
 * aggregates the outcome. Used by the Transfers → Media bulk-assign tool.
 */
export const bulkApplyTransferMedia = async (
  ids: string[],
  media: { photos: TransferPhoto[] | null; galleryImageMediaIds: string[] },
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const failed: { id: string; error: string }[] = [];
  let updated = 0;
  for (const id of ids) {
    try {
      await updateTransfer(id, media);
      updated += 1;
    } catch (err) {
      failed.push({ id, error: err instanceof Error ? err.message : 'Update failed' });
    }
  }
  return { updated, failed };
};
